import { useRef, useState, useMemo, useCallback } from 'react'
import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import useUserHasRoles from '@hook/useUserHasRoles'
import { useAccessUser } from '@hook/useAccessUser'
import { useDisabledHover } from '@hook/useDisabledHover'
import { ROLE_PARSE_ADMIN, ROLE_SUBSCRIPTION_ADMIN } from '@constants/roles'
import useCheckableItems from '@hook/useCheckableItems'
import useEffectWhenCondition from '@hook/useEffectWhenCondition'
import useDebounce from '@hook/useDebounce'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'react-toastify'

const collectionsPerPage = 20

const CollectionsViewModel = () => {
    
    const refCollectionsTable = useRef()
    
    const [isDeletingCollections, setIsDeletingCollections] = useState(false)
    
    const [pageIndex, setPageIndex] = useState(0)
    
    // const [collections, setCollections] = useState([])
    const [editCollection, setEditCollection] = useState()
    const [collectionsMarkedForDelete, setCollectionsMarkedForDelete] = useState([])
    
    // const [sortMethod, setSortMethod] = useState('asc')
    const [query, setQuery] = useState('')
    const [editCollectionDialogOpen, setEditCollectionDialogOpen] = useState(false)
    const [showAssignToUser, showHideAssignToUser] = useState(false)
    const [assignCollections, setAssignCollections] = useState([])
    const [deleteCollectionDialogOpen, setDeleteCollectionDialogOpen] = useState(false)
    
    const isParseAdmin = useUserHasRoles([
        ROLE_PARSE_ADMIN,
        ROLE_SUBSCRIPTION_ADMIN,
    ])
    
    const createCollection = useAccessUser({
        accessType: 'collections',
        accessName: 'create_new_collections',
    })
    const createCollectionHover = useDisabledHover(!createCollection)
    
    const user = useWireValue(store.user)
    const collections = useWireValue(store.collections)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const debouncedQuery = useDebounce(query, 500)
    
    const fetchCollections = useCallback(async (query, pageIndex, signal) => {
        
        return await actions.fetchCollections({
            query,
            offset: (pageIndex * collectionsPerPage),
            limit: collectionsPerPage,
            withCounts: true,
            signal,
            throwOnError: true,
        })
        
    }, [])
    
    const {
        // data: collections,
        error: collectionsError,
        isLoading: collectionsLoading,
    } = useQuery({
        queryKey: ['collections', debouncedQuery, query, pageIndex],
        queryFn: async ({ signal }) => fetchCollections(debouncedQuery, pageIndex, signal),
        enabled: true,
        retry: 0,
    })
    
    const {
        checked,
        allChecked,
        anyChecked,
        someChecked,
        hasChecked,
        toggleChecked,
        toggleAllChecked,
    } = useCheckableItems(collections?.rows ?? [])
    
    const onDeleteMultipleClick = useCallback(() => {
        
        setCollectionsMarkedForDelete(collections?.rows?.filter(it => checked.includes(it.id)))
        
        setDeleteCollectionDialogOpen(true)
        
    }, [checked, collections])
    
    const handleShowHideAssignToUser = async show => {
        
        showHideAssignToUser(show)
        
        if (!show)
            setAssignCollections([])
        
    }
    
    const showAddNewCollectionDialog = useCallback(() => {
        
        if (editCollection)
            setEditCollection(undefined)
        
        setEditCollectionDialogOpen(prev => !prev)
        
    }, [editCollection])
    
    const handleDelete = async () => {
        
        if (!collectionsMarkedForDelete?.id?.length) return
        
        try {
            await actions.deleteCollection(collectionsMarkedForDelete?.id)
            toast.success(`Collection "${collectionsMarkedForDelete.name}" deleted successfully`)
            await fetchCollections()
        } catch (e) {
            console.error('handleDelete', e)
            toast.error('Failed to delete collection. Please try again.')
        }
        
    }
    
    const handleDeleteMultiple = useCallback(async () => {
        
        if (!checked?.length) return
        
        setIsDeletingCollections(true)
        
        try {
            await actions.deleteCollections(checked)
            toggleAllChecked(false)
            toast.success(`Deleted ${checked.length} collections`)
        } catch (e) {
            console.error('handleDeleteMultiple', e)
            toast.error('Failed to delete collections. Please try again.')
        }
        
        setIsDeletingCollections(false)
        
    }, [checked])
    
    const filteredCollections = useMemo(() => {
        
        if (!query?.length) return collections
        
        const filtered = collections?.rows?.filter(it => it.name.toLowerCase().includes(query)) || []
        
        return {
            count: filtered?.length || 0,
            rows: filtered || [],
        }
        
    }, [query, collections])
    
    const scrollTopCollections = useCallback((smooth = true) => {
        
        refCollectionsTable.current?.scroll({
            top: 0,
            behavior: smooth ? 'smooth' : 'auto',
        })
        
    }, [refCollectionsTable])
    
    const hasCollections = useMemo(() => (
        filteredCollections?.count > 0 &&
        filteredCollections?.rows?.length > 0
    ), [filteredCollections])
    
    useEffectWhenCondition(
        () => window._hsq !== undefined,
        actions.identifyHubspotUser,
        [], 5000,
    )
    
    return {
        
        collectionsPerPage,
        
        // Refs
        refCollectionsTable,
        
        // State
        error: collectionsError,
        loading: collectionsLoading,
        isDeletingCollections,
        setIsDeletingCollections,
        pageIndex,
        setPageIndex,
        editCollection,
        setEditCollection,
        collectionsMarkedForDelete,
        setCollectionsMarkedForDelete,
        query,
        setQuery,
        editCollectionDialogOpen,
        setEditCollectionDialogOpen,
        showAssignToUser,
        showHideAssignToUser,
        assignCollections,
        setAssignCollections,
        deleteCollectionDialogOpen,
        setDeleteCollectionDialogOpen,
        
        // Global State
        user,
        collections,
        remoteConfig,
        
        // Memos
        isParseAdmin,
        createCollection,
        createCollectionHover,
        filteredCollections,
        hasCollections,
        
        // Methods
        onDeleteMultipleClick,
        handleShowHideAssignToUser,
        fetchCollections,
        showAddNewCollectionDialog,
        handleDelete,
        handleDeleteMultiple,
        scrollTopCollections,
        
        // Hooks
        checked,
        allChecked,
        anyChecked,
        someChecked,
        hasChecked,
        toggleChecked,
        toggleAllChecked,
        
    }
}

export default CollectionsViewModel
